<template>
  <div class="marketing-pressure-page">
    <!-- Page Header -->
    <div class="page-header">
            <!-- Back link -->
      <router-link
        :to="{ name: 'campaigns-automation-list' }"
        class="back-link"
      >
        <i class="fas fa-arrow-left" />
        <span>{{ t('automatedScenarios.actions.marketingPressure.backToCampaigns') }}</span>
      </router-link>

      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            {{ t('automatedScenarios.actions.marketingPressure.title') }}
          </h1>
          <p class="page-summary">
            {{ t('automatedScenarios.actions.marketingPressure.subtitle') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Page Content -->
    <div class="page-content">
      <!-- Info Message -->
      <SpmMessage severity="info">
        {{ t('automatedScenarios.actions.marketingPressure.interval.desc1') }}
        <br>
        {{ t('automatedScenarios.actions.marketingPressure.interval.desc2') }}
      </SpmMessage>

      <!-- Channels Configuration -->
      <div class="channels-section">
        <div class="channels-header">
          <SpmButton
            :label="t('automatedScenarios.actions.marketingPressure.resetDefaults')"
            :loading="isResetting"
            class-style="p-button p-button-text p-button-secondary"
            icon="fa fa-rotate-left"
            @click="confirmReset"
          />
        </div>
        <div class="channels-list">
          <div
            v-for="config in delayConfig"
            :key="config.key"
            class="channel-row"
          >
            <div class="channel-row__left">
              <div class="channel-row__icon">
                <i :class="config.icon" />
              </div>
              <div class="channel-row__info">
                <span class="channel-row__label">
                  {{ config.label }}
                  <ComingSoonBadge
                    v-if="config.service && getServiceFlag(config.service)?.comingSoon"
                    variant="inline"
                  />
                </span>
              </div>
            </div>
            <div class="channel-row__right mr-3">
              <PeriodSelector
                v-model="delayData[config.key]"
                :units="delayUnits"
                :min-value="0"
                title=""
              />
            </div>
          </div>
        </div>

        <!-- Action Footer -->
        <div class="action-footer">
          <SpmButton
            :label="t('templateBuilder.fields.validate')"
            :loading="isLoading"
            class-style="p-button p-button-primary"
            icon="fa fa-check"
            @click="updateConfig"
          />
        </div>
      </div>
    </div>
    <ConfirmDialog group="marketingPressureReset" />
  </div>
</template>

<script lang="ts">
import Button from 'primevue/button';
import {defineComponent, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted, reactive, ref,} from 'vue';
import { useI18n } from 'vue-i18n';
import PeriodSelector from '@/components/automated-scenarios/fields/PeriodSelector.vue';
import { ShopsConfiguration } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import {
  GetShopsConfigurationList,
  saveShopConfigurationsNest,
} from '@/composables/shop/ShopsConfiguration';
import { getConfigurationKeysLike } from '@/composables/configs/configuration';
import { Period } from '@/types/automated-scenarios';
import { TimeUnits } from '@/types';
import { showToastError, showToastSuccess } from '@/helpers';
import SpmButton from "@/components/spm-primevue/SpmButton.vue";
import { store } from '@/store';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { getFeatureFlagForService } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

export default defineComponent({
  name: 'MarketingPressure',
  components: {
    SpmButton,
    PeriodSelector,
    Button,
    SpmMessage,
    ConfirmDialog,
    ComingSoonBadge,
  },

  setup() {
    const { t } = useI18n();
    const isLoading = ref(false);
    const isResetting = ref(false);
    const confirm = useConfirm();
    const idShop = ref(UserState.activeShop?.id ?? 0);

    interface DelayData {
      email: Period;
      sms: Period;
      popUp: Period;
      push: Period;
      whatsapp: Period;
      richtexting: Period;
    }

    const channelKeys: Record<keyof DelayData, string> = {
      email: 'workflow_marketing_pressure_email_delay',
      sms: 'workflow_marketing_pressure_sms_delay',
      popUp: 'workflow_marketing_pressure_popup_delay',
      push: 'workflow_marketing_pressure_push_delay',
      whatsapp: 'workflow_marketing_pressure_whatsapp_delay',
      richtexting: 'workflow_marketing_pressure_richtexting_delay',
    };

    const delayData = reactive<DelayData>({
      email: { value: 0, unit: TimeUnits.MINUTE },
      sms: { value: 0, unit: TimeUnits.MINUTE },
      popUp: { value: 0, unit: TimeUnits.MINUTE },
      push: { value: 0, unit: TimeUnits.MINUTE },
      whatsapp: { value: 0, unit: TimeUnits.MINUTE },
      richtexting: { value: 0, unit: TimeUnits.MINUTE },
    })

    const delayConfig = ref([
      {
        key: 'email',
        label: t('templateBuilder.fields.email'),
        value: delayData.email,
        icon: 'fa-light fa-envelope',
      },
      {
        key: 'sms',
        label: t('automatedScenarios.actions.marketingPressure.input.sms'),
        value: delayData.sms,
        icon: 'fa-light fa-comment-sms',
      },
      {
        key: 'popUp',
        label: t('automatedScenarios.actions.marketingPressure.input.display'),
        value: delayData.popUp,
        icon: 'fa-light fa-window-restore',
      },
      {
        key: 'push',
        label: t('automatedScenarios.actions.marketingPressure.scenarios.push'),
        value: delayData.push,
        icon: 'fa-light fa-bell',
      },
      {
        key: 'whatsapp',
        label: t('automatedScenarios.actions.marketingPressure.scenarios.whatsapp'),
        value: delayData.whatsapp,
        icon: 'fa-brands fa-whatsapp',
        service: 'whatsapp_business',
      },
      {
        key: 'richtexting',
        label: t('automatedScenarios.actions.marketingPressure.scenarios.richtexting'),
        value: delayData.richtexting,
        icon: 'fa-light fa-comment-dots',
        service: 'rcs',
      },
    ])

    const delayUnits = [
      { value: TimeUnits.MINUTE, label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: TimeUnits.HOUR, label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: TimeUnits.DAY, label: t('automatedScenarios.fields.durations.day(s)') },
    ];


    const updateConfig = async() => {
      isLoading.value = true;
      // Enregistrement via NestJS (POST /shop/configurations) : il vide aussi le
      // cache Redis de la pression lu par le moteur, sinon l'ancien délai reste
      // appliqué jusqu'à 1 h.
      const saved = await saveShopConfigurationsNest(
        idShop.value,
        (Object.keys(channelKeys) as Array<keyof DelayData>).map((channel) => ({
          key: channelKeys[channel],
          value: JSON.stringify({ value: String(delayData[channel].value), unit: delayData[channel].unit }),
          lang: '',
        })),
      );
      if (saved) {
        showToastSuccess('savedSuccessful');
      } else {
        showToastError('noSave');
      }
      isLoading.value = false;
    }

    const parsePeriod = (raw: string): Period | null => {
      try {
        return JSON.parse(raw) as Period;
      } catch {
        return null;
      }
    };

    const fetchDefaults = async (): Promise<Record<keyof DelayData, Period>> => {
      const channels = Object.keys(channelKeys) as Array<keyof DelayData>;
      const defaults = await getConfigurationKeysLike('workflow_marketing_pressure_%_delay_default');
      const defaultByKey = new Map<string, string>(defaults.map((item) => [item.key, item.value]));

      const result = {} as Record<keyof DelayData, Period>;
      channels.forEach((channel) => {
        const raw = defaultByKey.get(`${channelKeys[channel]}_default`);
        const period = raw ? parsePeriod(raw) : null;
        result[channel] = period ?? { value: 0, unit: TimeUnits.MINUTE };
      });
      return result;
    };

    const resetToDefaults = async () => {
      isResetting.value = true;
      try {
        const defaults = await fetchDefaults();
        const channels = Object.keys(channelKeys) as Array<keyof DelayData>;
        const saved = await saveShopConfigurationsNest(
          idShop.value,
          channels.map((channel) => ({
            key: channelKeys[channel],
            value: JSON.stringify({ value: String(defaults[channel].value), unit: defaults[channel].unit }),
            lang: '',
          })),
        );
        if (!saved) {
          showToastError('noSave');
          return;
        }
        channels.forEach((channel) => {
          delayData[channel] = defaults[channel];
        });
        showToastSuccess('savedSuccessful');
      } catch {
        showToastError('noSave');
      } finally {
        isResetting.value = false;
      }
    };

    const confirmReset = () => {
      confirm.require({
        message: t('automatedScenarios.actions.marketingPressure.resetConfirm'),
        header: t('automatedScenarios.actions.marketingPressure.resetDefaults'),
        group: 'marketingPressureReset',
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-secondary',
        accept: resetToDefaults,
      });
    };

    onBeforeMount(async () => {
      store.commit('general/showTheSpinner');

      const channels = Object.keys(channelKeys) as Array<keyof DelayData>;
      const keyToChannel = new Map<string, keyof DelayData>(
        channels.map((channel) => [channelKeys[channel], channel]),
      );

      const shopConfigs = await GetShopsConfigurationList({
        shopId: idShop.value,
        key: 'workflow_marketing_pressure_%_delay',
        fields: 'id_shop_configuration, key, value',
        limit: 0,
        offset: 0,
      });

      const channelsWithShopValue = new Set<keyof DelayData>();
      if (shopConfigs) {
        shopConfigs.forEach((record: ShopsConfiguration) => {
          const channel = keyToChannel.get(record.key);
          if (!channel) return;
          const period = parsePeriod(record.value);
          if (!period) return;
          delayData[channel] = period;
          channelsWithShopValue.add(channel);
        });
      }

      const missingChannels = channels.filter((c) => !channelsWithShopValue.has(c));
      if (missingChannels.length > 0) {
        const defaults = await getConfigurationKeysLike('workflow_marketing_pressure_%_delay_default');
        const defaultByKey = new Map<string, string>(defaults.map((item) => [item.key, item.value]));
        missingChannels.forEach((channel) => {
          const raw = defaultByKey.get(`${channelKeys[channel]}_default`);
          if (!raw) return;
          const period = parsePeriod(raw);
          if (period) delayData[channel] = period;
        });
      }

      store.commit('general/hideTheSpinner');
    });

    return {
      t,
      isLoading,
      isResetting,
      delayConfig,
      delayData,
      delayUnits,
      updateConfig,
      confirmReset,
      getServiceFlag: getFeatureFlagForService,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page Layout */
.marketing-pressure-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Page Header */
.page-header {
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--secondary-700);
  }

  i {
    font-size: 0.75rem;
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  line-height: 1.2;
}

.page-summary {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin: 0;
}

/* Page Content */
.page-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-message {
  margin: 0;
}

/* Channels Section */
.channels-section {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.channels-header {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.channels-list {
  display: flex;
  flex-direction: column;
}

/* Channel Row - compact list style */
.channel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fafafa;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    min-width: 0;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    flex-shrink: 0;
    transition: background-color 0.2s ease;

    i {
      font-size: 1.125rem;
      color: #64748b;
    }
  }

  &:hover &__icon {
    background: #f1f5f9;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__label {
    font-size: 0.9375rem;
    font-weight: 500;
    color: #334155;
  }

  &__right {
    flex-shrink: 0;
    width: 220px;
  }
}

/* Action Footer */
.action-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  background: #fafafa;
  border-top: 1px solid #e5e7eb;
}

/* Period Selector Overrides */
.channel-row__right :deep(.period-selector-grouped) {
  width: 100%;
}

.channel-row__right :deep(.p-inputgroup) {
  border-radius: 6px;
  overflow: hidden;
}

.channel-row__right :deep(.p-inputnumber-input) {
  border-radius: 6px 0 0 6px;
  border-right: none;
  text-align: center;
  width: 60px;
}

.channel-row__right :deep(.p-dropdown) {
  border-radius: 0 6px 6px 0;
  min-width: 120px;
  width: 120px;
}

.channel-row__right :deep(.p-inputtext:focus),
.channel-row__right :deep(.p-dropdown:focus) {
  box-shadow: 0 0 0 0.2rem rgba(148, 200, 64, 0.25);
  border-color: #94c840;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .channel-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;

    &__right {
      width: 100%;
    }
  }

  .action-footer {
    justify-content: stretch;
    padding: 1rem;

    :deep(.p-button) {
      width: 100%;
    }
  }
}
</style>
